/**
 * ============================================================================
 * TWO-FACTOR AUTHENTICATION SETUP MODAL
 * ============================================================================
 * 
 * Modal for enabling/disabling TOTP 2FA on user account.
 * 
 * Features:
 * - QR code display for authenticator app
 * - Manual entry key
 * - 6-digit code verification
 * - Backup codes display and download
 * - Disable 2FA option
 * 
 * Location: web-app/src/components/profile/TwoFactorModal.tsx
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  useToast,
  Spinner,
  PinInput,
  PinInputField,
  Alert,
  AlertIcon,
  Divider,
  Image,
} from '@chakra-ui/react';
import twoFactorService, { TwoFactorStatus, TwoFactorSetupData } from '../../services/twoFactorService';

interface TwoFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStatusChange?: () => void;
}

type Step = 'status' | 'setup' | 'verify' | 'backup' | 'disable';

const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
  isOpen,
  onClose,
  onStatusChange,
}) => {
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TwoFactorStatus>({ enabled: false });
  const [step, setStep] = useState<Step>('status');
  const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState('');

  // Load 2FA status when modal opens
  useEffect(() => {
    if (isOpen) {
      loadStatus();
    } else {
      // Reset state when closed
      setStep('status');
      setSetupData(null);
      setVerifyCode('');
      setDisableCode('');
      setBackupCodes([]);
      setError('');
    }
  }, [isOpen]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const s = await twoFactorService.get2FAStatus();
      setStatus(s);
    } catch (err) {
      console.error('Error loading 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await twoFactorService.setup2FA();
      setSetupData(data);
      setStep('setup');
    } catch (err: any) {
      setError(err.message || 'Failed to start 2FA setup');
      toast({
        title: 'Setup failed',
        description: err.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      const result = await twoFactorService.verify2FASetup(verifyCode);
      
      if (result.success && result.backupCodes) {
        setBackupCodes(result.backupCodes);
        setStep('backup');
        setStatus({ ...status, enabled: true });
        onStatusChange?.();
        toast({
          title: '2FA Enabled!',

          description: 'Your account is now protected with two-factor authentication.',
          status: 'success',
          duration: 4000,
        });
      } else {
        setError(result.error || 'Invalid code. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      const result = await twoFactorService.disable2FA(disableCode);
      
      if (result.success) {
        setStatus({ enabled: false });
        setStep('status');
        onStatusChange?.();
        toast({
          title: '2FA Disabled',
          description: 'Two-factor authentication has been turned off.',
          status: 'info',
          duration: 3000,
        });
      } else {
        setError(result.error || 'Invalid code. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to disable 2FA');
    } finally {
      setVerifying(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast({
      title: 'Copied!',
      description: 'Backup codes copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const downloadBackupCodes = () => {
    const content = `PokerSweepStars Backup Codes
============================
Generated: ${new Date().toLocaleDateString()}

Store these codes in a safe place. Each code can only be used once.

${backupCodes.join('\n')}

If you lose access to your authenticator app, use one of these codes to sign in.
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pokersweepstars-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (step === 'backup') {
      // Don't allow closing until backup codes are acknowledged
      toast({
        title: 'Save your backup codes',
        description: 'Please save your backup codes before closing.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    onClose();
  };

  const inputStyles = {
    bg: '#1C1C1E',
    border: 'none',
    borderRadius: 'xl',
    color: 'white',
    textAlign: 'center' as const,
    fontSize: 'xl',
    fontFamily: 'mono',
    _focus: { 
      boxShadow: '0 0 0 2px rgba(255, 107, 53, 0.5)',
      bg: '#252528',
    },
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Text
            color="gray.400"
            fontSize="md"
            cursor="pointer"
            onClick={handleClose}
            _hover={{ color: 'white' }}
          >
            {step === 'backup' ? '' : 'Cancel'}
          </Text>
          <Text color="white" fontSize="md" fontWeight="semibold">
            Two-Factor Authentication
          </Text>
          <Box w="50px" />
        </HStack>

        <ModalBody py={6} px={4} overflowY="auto">
          <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner color="orange.400" size="xl" />
                <Text color="gray.400" mt={4}>Loading...</Text>
              </Box>
            ) : (
              <>
                {/* STATUS STEP */}
                {step === 'status' && (
                  <>
                    {/* Current Status */}
                    <Box 
                      bg={status.enabled ? 'green.900' : '#1C1C1E'}
                      p={6} 
                      borderRadius="xl"
                      borderLeft="4px solid"
                      borderColor={status.enabled ? 'green.400' : 'gray.600'}
                      textAlign="center"
                    >
                      <Text fontSize="4xl" mb={2}>
                        {status.enabled ? '' : ''}

                      </Text>
                      <Text color="white" fontSize="xl" fontWeight="bold">
                        {status.enabled ? '2FA Enabled' : '2FA Not Enabled'}
                      </Text>
                      <Text color={status.enabled ? 'green.300' : 'gray.400'} fontSize="sm" mt={2}>
                        {status.enabled 
                          ? 'Your account is protected with two-factor authentication'
                          : 'Add an extra layer of security to your account'
                        }
                      </Text>
                      {status.enabled && status.backupCodesRemaining !== undefined && (
                        <Text color="orange.400" fontSize="xs" mt={2}>
                          {status.backupCodesRemaining} backup codes remaining
                        </Text>
                      )}
                    </Box>

                    {/* Info Box */}
                    <Box bg="#1C1C1E" p={4} borderRadius="xl">
                      <Text color="gray.400" fontSize="sm" fontWeight="semibold" mb={2}>
                        What is 2FA?
                      </Text>
                      <Text color="gray.500" fontSize="sm">
                        Two-factor authentication adds an extra layer of security by requiring 
                        a code from your phone in addition to your password when signing in.
                      </Text>
                    </Box>

                    {/* Compatible Apps */}
                    <Box bg="#1C1C1E" p={4} borderRadius="xl">
                      <Text color="gray.400" fontSize="sm" fontWeight="semibold" mb={2}>
                        Compatible Apps
                      </Text>
                      <VStack align="start" spacing={1}>
                        <Text color="gray.500" fontSize="sm">• Google Authenticator</Text>
                        <Text color="gray.500" fontSize="sm">• Authy</Text>
                        <Text color="gray.500" fontSize="sm">• Microsoft Authenticator</Text>
                        <Text color="gray.500" fontSize="sm">• 1Password</Text>
                      </VStack>
                    </Box>

                    {/* Action Button */}
                    {status.enabled ? (
                      <Box
                        as="button"
                        w="100%"
                        py={4}
                        bg="red.600"
                        color="white"
                        borderRadius="xl"
                        fontWeight="semibold"
                        _hover={{ bg: 'red.500' }}
                        onClick={() => setStep('disable')}
                      >
                        Disable 2FA
                      </Box>
                    ) : (
                      <Box
                        as="button"
                        w="100%"
                        py={4}
                        bg="orange.500"
                        color="white"
                        borderRadius="xl"
                        fontWeight="semibold"
                        _hover={{ bg: 'orange.400' }}
                        onClick={handleStartSetup}
                      >
                        Enable 2FA
                      </Box>
                    )}
                  </>
                )}

                {/* SETUP STEP - Show QR Code */}
                {step === 'setup' && setupData && (
                  <>
                    <Box textAlign="center">
                      <Text color="white" fontSize="lg" fontWeight="bold" mb={2}>
                        Scan QR Code
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        Open your authenticator app and scan this QR code
                      </Text>
                    </Box>

                    {/* QR Code */}
                    <Box 
                      bg="white" 
                      p={4} 
                      borderRadius="xl" 
                      mx="auto"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Image 
                        src={setupData.qrCodeDataUrl} 
                        alt="2FA QR Code"
                        w="200px"
                        h="200px"
                      />
                    </Box>

                    {/* Manual Entry */}
                    <Box bg="#1C1C1E" p={4} borderRadius="xl">
                      <Text color="gray.400" fontSize="xs" mb={2}>
                        Can't scan? Enter this code manually:
                      </Text>
                      <Text 
                        color="orange.400" 
                        fontSize="md" 
                        fontFamily="mono"
                        fontWeight="bold"
                        wordBreak="break-all"
                        textAlign="center"
                        bg="#252528"
                        p={3}
                        borderRadius="lg"
                        cursor="pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(setupData.manualEntryKey);
                          toast({
                            title: 'Copied!',
                            status: 'success',
                            duration: 2000,
                          });
                        }}
                        _hover={{ bg: '#2A2A2E' }}
                      >
                        {setupData.manualEntryKey}
                      </Text>
                      <Text color="gray.600" fontSize="xs" mt={2} textAlign="center">
                        Tap to copy
                      </Text>
                    </Box>

                    <Box
                      as="button"
                      w="100%"
                      py={4}
                      bg="orange.500"
                      color="white"
                      borderRadius="xl"
                      fontWeight="semibold"
                      _hover={{ bg: 'orange.400' }}
                      onClick={() => setStep('verify')}
                    >
                      Continue
                    </Box>
                  </>
                )}

                {/* VERIFY STEP - Enter Code */}
                {step === 'verify' && (
                  <>
                    <Box textAlign="center">
                      <Text color="white" fontSize="lg" fontWeight="bold" mb={2}>
                        Enter Verification Code
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        Enter the 6-digit code from your authenticator app
                      </Text>
                    </Box>

                    {error && (
                      <Alert status="error" bg="red.900" borderRadius="xl">
                        <AlertIcon color="red.300" />
                        <Text color="red.200" fontSize="sm">{error}</Text>
                      </Alert>
                    )}

                    {/* PIN Input */}
                    <HStack justify="center" spacing={2}>
                      <PinInput 
                        value={verifyCode}
                        onChange={setVerifyCode}
                        otp
                        size="lg"
                        autoFocus
                      >
                        {[0,1,2,3,4,5].map(i => (
                          <PinInputField 
                            key={i}
                            {...inputStyles}
                            w="50px"
                            h="60px"
                          />
                        ))}
                      </PinInput>
                    </HStack>

                    <Box
                      as="button"
                      w="100%"
                      py={4}
                      bg="orange.500"
                      color="white"
                      borderRadius="xl"
                      fontWeight="semibold"
                      opacity={verifying || verifyCode.length !== 6 ? 0.5 : 1}
                      cursor={verifying || verifyCode.length !== 6 ? 'not-allowed' : 'pointer'}
                      _hover={verifying || verifyCode.length !== 6 ? {} : { bg: 'orange.400' }}
                      onClick={handleVerifySetup}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {verifying ? <Spinner size="sm" mr={2} /> : null}
                      {verifying ? 'Verifying...' : 'Verify & Enable'}
                    </Box>

                    <Text 
                      color="gray.500" 
                      fontSize="sm" 
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: 'gray.400' }}
                      onClick={() => setStep('setup')}
                    >
                      ← Back to QR code
                    </Text>
                  </>
                )}

                {/* BACKUP CODES STEP */}
                {step === 'backup' && (
                  <>
                    <Box textAlign="center">
                      <Text fontSize="4xl" mb={2}></Text>

                      <Text color="green.400" fontSize="lg" fontWeight="bold" mb={2}>
                        2FA Successfully Enabled!
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        Save these backup codes in a safe place
                      </Text>
                    </Box>

                    <Alert status="warning" bg="orange.900" borderRadius="xl">
                      <AlertIcon color="orange.300" />
                      <Text color="orange.200" fontSize="sm">
                        These codes can be used to access your account if you lose your phone. 
                        Each code can only be used once.
                      </Text>
                    </Alert>

                    {/* Backup Codes Grid */}
                    <Box bg="#1C1C1E" p={4} borderRadius="xl">
                      <VStack spacing={2}>
                        {backupCodes.map((code, i) => (
                          <HStack key={i} justify="space-between" w="100%">
                            <Text color="gray.500" fontSize="sm">{i + 1}.</Text>
                            <Text 
                              color="white" 
                              fontFamily="mono" 
                              fontSize="md"
                              fontWeight="bold"
                            >
                              {code}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>

                    {/* Action Buttons */}
                    <HStack spacing={3}>
                      <Box
                        as="button"
                        flex={1}
                        py={3}
                        bg="#252528"
                        color="white"
                        borderRadius="xl"
                        fontWeight="semibold"
                        _hover={{ bg: '#2A2A2E' }}
                        onClick={copyBackupCodes}
                      >
                         Copy

                      </Box>
                      <Box
                        as="button"
                        flex={1}
                        py={3}
                        bg="#252528"
                        color="white"
                        borderRadius="xl"
                        fontWeight="semibold"
                        _hover={{ bg: '#2A2A2E' }}
                        onClick={downloadBackupCodes}
                      >
                         Download

                      </Box>
                    </HStack>

                    <Box
                      as="button"
                      w="100%"
                      py={4}
                      bg="green.600"
                      color="white"
                      borderRadius="xl"
                      fontWeight="semibold"
                      _hover={{ bg: 'green.500' }}
                      onClick={onClose}
                    >
                      I've Saved My Codes
                    </Box>
                  </>
                )}

                {/* DISABLE STEP */}
                {step === 'disable' && (
                  <>
                    <Box textAlign="center">
                      <Text color="white" fontSize="lg" fontWeight="bold" mb={2}>
                        Disable 2FA
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        Enter a code from your authenticator app to confirm
                      </Text>
                    </Box>

                    <Alert status="warning" bg="red.900" borderRadius="xl">
                      <AlertIcon color="red.300" />
                      <Text color="red.200" fontSize="sm">
                        Disabling 2FA will make your account less secure. 
                        Make sure you have other security measures in place.
                      </Text>
                    </Alert>

                    {error && (
                      <Alert status="error" bg="red.900" borderRadius="xl">
                        <AlertIcon color="red.300" />
                        <Text color="red.200" fontSize="sm">{error}</Text>
                      </Alert>
                    )}

                    {/* PIN Input */}
                    <HStack justify="center" spacing={2}>
                      <PinInput 
                        value={disableCode}
                        onChange={setDisableCode}
                        otp
                        size="lg"
                        autoFocus
                      >
                        {[0,1,2,3,4,5].map(i => (
                          <PinInputField 
                            key={i}
                            {...inputStyles}
                            w="50px"
                            h="60px"
                          />
                        ))}
                      </PinInput>
                    </HStack>

                    <Box
                      as="button"
                      w="100%"
                      py={4}
                      bg="red.600"
                      color="white"
                      borderRadius="xl"
                      fontWeight="semibold"
                      opacity={verifying || disableCode.length !== 6 ? 0.5 : 1}
                      cursor={verifying || disableCode.length !== 6 ? 'not-allowed' : 'pointer'}
                      _hover={verifying || disableCode.length !== 6 ? {} : { bg: 'red.500' }}
                      onClick={handleDisable}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {verifying ? <Spinner size="sm" mr={2} /> : null}
                      {verifying ? 'Disabling...' : 'Disable 2FA'}
                    </Box>

                    <Text 
                      color="gray.500" 
                      fontSize="sm" 
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: 'gray.400' }}
                      onClick={() => {
                        setStep('status');
                        setDisableCode('');
                        setError('');
                      }}
                    >
                      ← Cancel
                    </Text>
                  </>
                )}

                {/* Spacer for safe area */}
                <Box h="80px" />
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TwoFactorModal;